/*
 * A dose that was given and does not count towards its course (KIN-1923, backend KIN-1873).
 *
 * NIAC Chapter 2 §2.2.6: a dose given more than four days before its minimum age or interval is
 * not a valid dose. It stays on the record, it is still reported to NIIS and still claimed from
 * PCRS — but it does not fill its schedule slot, and a repeat is due at least four weeks later.
 *
 * `disregardedAt` being non-null is the whole marker, and both fields are read-only. The state is
 * derived at save from the same rules that raise the warning the clinician already saw, and
 * re-derived on every save — so correcting a mistyped date clears it by itself. There is no way
 * to set or clear it from here by design: acknowledging the early-dose warning confirms the dose
 * was given early, it does not vote on whether it counts.
 *
 * What this must never look like: cancelled, failed, or in error. The vaccine was given, the
 * report went and the claim went; nothing is outstanding on either receiver and nothing needs
 * chasing. Nor is it a data-entry mistake to correct — if the date is right, the repeat is real
 * clinical work.
 */

export const isDisregardedDose = (immunisation) => !!immunisation?.disregardedAt;

/*
 * The backend writes `disregardedReason` to be shown verbatim — which minimum the dose fell
 * short of, in words, under 250 characters. It is not reworded here: the sentence is the
 * clinical justification, and paraphrasing it client-side would put this screen's wording and
 * the rule it cites out of step the moment either changed.
 *
 * The fallback covers a dose flagged without a reason, which should not happen but must not
 * render as an unexplained mark: a dose that silently looks different from its neighbours is
 * worse than one that says plainly it does not count.
 */
export const formatDisregardedReason = (immunisation) => {
  if (!isDisregardedDose(immunisation)) return null;

  return (
    immunisation.disregardedReason?.trim() ||
    "Given too early to count towards the course (NIAC Chapter 2 §2.2.6)."
  );
};
